import { Handle, Position } from '@xyflow/react'
import { isFired } from './useFiredFlash'
import { OperandRow } from './OperandRow'
import { LOGICAL_ACCENT } from './operatorTheme'

/**
 * Logical operators — one component for all three node types, keyed by `type`.
 * `and` / `or` take two inputs (A, B); `not` takes one (A). Each input is a pin
 * OR an inline literal box (see OperandRow), then coerced to a bool
 * (src/lib/compareValues.js toBoolean: a "false"/"0"/"" string is false, not
 * truthy). Result is a bool on out-value. The operator is the NODE TYPE, not
 * editable config.
 */

const OP = {
  and: { sym: '&&', label: 'And',  unary: false },
  or:  { sym: '||', label: 'Or',   unary: false },
  not: { sym: '!',  label: 'Not',  unary: true  },
}

const ACCENT = LOGICAL_ACCENT
const HEADER = '#2a1e06'
const BOOL   = '#d06060'   // bool pins (Unreal convention) — logical ops are bool-in, bool-out

export function LogicalNode({ id, type, data, selected }) {
  const meta    = OP[type] ?? OP.and
  const fired   = isFired(data)
  const preview = data._preview
  const pins    = meta.unary ? ['a'] : ['a', 'b']

  return (
    <div style={{
      background: selected ? '#201804' : '#120d02',
      border: `2px solid ${fired ? '#ffcc44' : selected ? ACCENT : '#4a3a10'}`,
      borderRadius: 6, minWidth: 180, fontFamily: 'var(--m-font)', fontSize: 12,
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : selected ? `0 0 12px ${ACCENT}33` : 'none',
    }}>
      <div style={{
        background: HEADER,
        padding: '7px 14px 7px 16px',
        display: 'flex', alignItems: 'center', gap: 8,
        borderBottom: `1px solid ${ACCENT}33`, borderRadius: '4px 4px 0 0',
        position: 'relative',
      }}>
        <span style={{ color: ACCENT, fontWeight: 'bold', fontSize: 13, fontFamily: 'var(--m-mono)' }}>{meta.sym}</span>
        <span style={{ color: ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>{meta.label}</span>
        {/* Output (bool) pin — ReactFlow's default right-edge placement. To nudge
            it, add e.g. `right: N` to this style. Kept dead simple on purpose. */}
        <Handle type="source" position={Position.Right} id="out-value"
          style={{ background: BOOL, width: 10, height: 10, borderRadius: '50%' }} />
      </div>

      {pins.map(pin => (
        <OperandRow key={pin}
          nodeId={id} pinId={`in-${pin}`} label={pin.toUpperCase()}
          accent={ACCENT} pinColor={BOOL} borderColor="#241a06"
          value={data[pin]} onChange={v => data.onChange?.(pin, v)} />
      ))}

      {preview !== undefined && (
        <div style={{ padding: '5px 14px 7px 16px', color: preview ? '#7ad29a' : '#c08080', fontSize: 10 }}>
          → {String(preview)}
        </div>
      )}
    </div>
  )
}
